import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import GlobalStyles from '@nebula/global-styles';
import PropTypes from 'prop-types';

import PaymentOrchestrator from '../../../components/PaymentOrchestrator';
import ProcessingSpinner from '../../../components/PaymentProcessing/ProcessingSpinner';
import resolveFlexBillingFrequency, { isAnnually } from '../../../helpers/flex-helpers/flexBillingFrequency';

const ChangeBillingPeriodPage = ({ upgradePlan, targetPeriod, isFlexFlow, currentFlexSubscription, isWebview }) => {
  const history = useHistory();
  const subscription = useSelector(state => state.subscriptions.current);
  const legacyBillingFrequency = subscription?.terms?.[0]?.billingFrequency;
  const flexBillingFrequency = isFlexFlow
    ? resolveFlexBillingFrequency({
        term: currentFlexSubscription?.termFrequency ?? currentFlexSubscription?.billingFrequency,
        planCode: currentFlexSubscription?.planCode,
      })
    : null;
  const billingFrequency = flexBillingFrequency ?? legacyBillingFrequency;

  const isCurrentlyAnnual = isAnnually(billingFrequency);

  const isSwitchingToAnnual = targetPeriod === 'annual';

  // Don't allow switching to the same period
  if ((isCurrentlyAnnual && isSwitchingToAnnual) || (!isCurrentlyAnnual && !isSwitchingToAnnual)) {
    history.replace('/');
    return null;
  }

  return (
    <ThemeProvider theme={nebulaTheme}>
      <GlobalStyles />
      {upgradePlan?.product ? (
        <PaymentOrchestrator
          upgradePlan={upgradePlan}
          isTrial={false}
          isBillingPeriodChange
          targetBillingPeriod={targetPeriod}
          isFlexFlow={isFlexFlow}
          currentFlexSubscription={currentFlexSubscription}
          isWebview={isWebview}
        />
      ) : (
        <ProcessingSpinner isWebview={isWebview} />
      )}
    </ThemeProvider>
  );
};

ChangeBillingPeriodPage.propTypes = {
  upgradePlan: PropTypes.shape({
    family: PropTypes.string,
    product: PropTypes.shape({
      planCode: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      trialLength: PropTypes.number,
      term: PropTypes.string,
    }),
  }),
  targetPeriod: PropTypes.oneOf(['monthly', 'annual']).isRequired,
  isFlexFlow: PropTypes.bool,
  currentFlexSubscription: PropTypes.shape({
    family: PropTypes.string,
    planCode: PropTypes.string,
    freeTrial: PropTypes.bool,
    termFrequency: PropTypes.string,
    billingFrequency: PropTypes.string,
  }),
  isWebview: PropTypes.bool,
};

ChangeBillingPeriodPage.defaultProps = {
  upgradePlan: null,
  isFlexFlow: false,
  currentFlexSubscription: null,
  isWebview: false,
};

export default ChangeBillingPeriodPage;
